/**
 * Shared mid-panel filters applied across every sales-exception config
 * that ships a Compare-Sales button (`rowDetail.type === 'compare-sales'`).
 *
 * Reviewers triaging the closed-with-balance / rounding-mismatch / LS-Xero
 * mismatch lists asked for three lenses that should look and behave the
 * same on every report:
 *
 *   1. Sale-date range — scope to a window of LS sale dates.
 *   2. LS status       — Closed / Open / Voided (the LS sale's own status,
 *                        not the exception's open/resolved state).
 *   3. Has Xero credit — toggle that filters to sales whose linked Xero
 *                        invoice carries a credit-note allocation
 *                        (`xero_amount_credited > 0`).
 *
 * Each filter resolves server-side via `buildSalesFilterParams` so
 * pagination counts stay honest on server-paginated reports (the
 * compare-sales rollup uses `serverTable`).
 *
 * Adding the filters to a config:
 *
 *   import { sharedSalesFilters, buildSalesFilterParams } from './sharedSalesFilters'
 *
 *   filters: [
 *     { key: 'search', type: 'search', label: 'Search', placeholder: '...' },
 *     ...sharedSalesFilters,
 *   ],
 *   buildFetchParams: (urlState) => ({
 *     ...existingParams,
 *     ...buildSalesFilterParams(urlState.filterValues),
 *   }),
 */
import React from 'react'
import type { ExceptionFilterDef } from '../types'
import { LightspeedIcon } from '../../../components/SystemIcons'
import CreditNoteBadge from '../../../components/shared/CreditNoteBadge'

const lsIcon = React.createElement(LightspeedIcon, { size: 14 })

// Toggle label: "Xero invoice has [CN]" with the [CN] rendered using the
// same yellow badge the compare-sales table uses. The plain `label`
// stays a readable string for surfaces that show active-filter chips.
const hasXeroCreditLabelNode = React.createElement(
  React.Fragment,
  null,
  'Xero invoice has ',
  React.createElement(CreditNoteBadge, { size: 'md' }),
)

export const SHARED_SALES_FILTER_KEYS = {
  saleDateRange: 'saleDateRange',
  lsStatus: 'lsStatus',
  hasXeroCredit: 'hasXeroCredit',
} as const

export const sharedSalesFilters: ExceptionFilterDef[] = [
  {
    key: SHARED_SALES_FILTER_KEYS.saleDateRange,
    type: 'daterange',
    label: 'Sale date',
    defaultValue: ['', ''],
  },
  {
    key: SHARED_SALES_FILTER_KEYS.lsStatus,
    type: 'select',
    label: 'Sale Status',
    defaultValue: 'all',
    // Three canonical LS sale-status buckets cover every raw value LS
    // emits. 'open' includes ONACCOUNT (by far the most common LS open
    // state - ~35k rows) plus OPEN, SAVED, AWAITING_PICKUP /
    // AWAITING_DISPATCH, SERVICE, LAYBY. 'closed' includes CLOSED and
    // every *_CLOSED variant (ONACCOUNT_CLOSED is the second-largest
    // LS bucket overall). 'voided' is the cancelled bucket. The
    // backend resolves each value via a UPPER(s.status) suffix pattern
    // so future LS status additions land in the right bucket without
    // a code change here.
    options: [
      { value: 'all', label: 'Any status' },
      { value: 'open', label: 'Open', icon: lsIcon },
      { value: 'closed', label: 'Closed', icon: lsIcon },
      { value: 'voided', label: 'Voided', icon: lsIcon },
    ],
  },
  {
    key: SHARED_SALES_FILTER_KEYS.hasXeroCredit,
    type: 'toggle',
    label: 'Xero invoice has credit note',
    labelNode: hasXeroCreditLabelNode,
    defaultEnabled: false,
  },
]

/**
 * Translate the shared-sales filter values into backend query params.
 * Empty / default values are omitted so URLs and request payloads stay
 * minimal.
 */
export const buildSalesFilterParams = (
  filterValues: Record<string, unknown> | undefined,
): Record<string, string | boolean> => {
  const params: Record<string, string | boolean> = {}
  if (!filterValues) return params

  const range = filterValues[SHARED_SALES_FILTER_KEYS.saleDateRange]
  if (Array.isArray(range)) {
    const [start, end] = range as [string, string]
    if (start) params.start_date = start
    if (end) params.end_date = end
  }

  const ls = filterValues[SHARED_SALES_FILTER_KEYS.lsStatus]
  if (typeof ls === 'string' && ls && ls !== 'all') {
    params.ls_status = ls
  }

  if (filterValues[SHARED_SALES_FILTER_KEYS.hasXeroCredit] === true) {
    params.has_xero_credit = true
  }

  return params
}
